import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Badge, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { BarChart, Bar, PieChart, Pie, LineChart, Line, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { getCurrentMembershipYear, getAvailableMembershipYears } from '../../utils/membershipYear';

const COLORS = ['#006e39', '#0077b6', '#e76f51', '#2a9d8f', '#f4a261', '#9c27b0', '#ff9800', '#607d8b'];

const SdaDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [copySuccess, setCopySuccess] = useState(false);

  // Dashboard data
  const [summary, setSummary] = useState(null);
  const [membersByRegion, setMembersByRegion] = useState([]);
  const [membersByTerritory, setMembersByTerritory] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [trends, setTrends] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedSda, setSelectedSda] = useState('');
  const [selectedSport, setSelectedSport] = useState(''); // Sotto-filtro per sport specifico
  const [selectedYear, setSelectedYear] = useState(getCurrentMembershipYear().toString()); // Anno sociale di riferimento
  const [showAllAssociations, setShowAllAssociations] = useState(false);

  // Elenco dinamico degli anni sociali disponibili per la dropdown
  const availableYears = useMemo(() => getAvailableMembershipYears(), []);
  const [availableSdas, setAvailableSdas] = useState([]);
  const [availableSports, setAvailableSports] = useState([]); // Sport disponibili per l'SDA selezionato
  const [yearComparison, setYearComparison] = useState(null);

  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      // Build query params
      const params = new URLSearchParams();
      if (selectedSda) {
        params.append('sda', selectedSda);
      }
      if (selectedSport) {
        params.append('sport', selectedSport);
      }
      if (selectedYear) {
        params.append('year', selectedYear);
      }

      // Fetch summary
      const summaryUrl = `/sda/dashboard/summary${params.toString() ? `?${params.toString()}` : ''}`;
      const summaryRes = await api.get(summaryUrl);
      setSummary(summaryRes.data.data);

      // Fetch members by region
      const regionUrl = `/sda/dashboard/members-by-region${params.toString() ? `?${params.toString()}` : ''}`;
      const regionRes = await api.get(regionUrl);
      setMembersByRegion(regionRes.data.data.regions);

      // Fetch members by territory
      const territoryParams = new URLSearchParams(params);
      if (selectedRegion) {
        territoryParams.append('region', selectedRegion);
      }
      const territoryUrl = `/sda/dashboard/members-by-territory${territoryParams.toString() ? `?${territoryParams.toString()}` : ''}`;
      const territoryRes = await api.get(territoryUrl);
      setMembersByTerritory(territoryRes.data.data.territories);

      // Fetch top associations
      const limit = showAllAssociations ? 100 : 10;
      const assocParams = new URLSearchParams(params);
      assocParams.append('limit', limit);
      const assocUrl = `/sda/dashboard/associations?${assocParams.toString()}`;
      const assocRes = await api.get(assocUrl);
      setAssociations(assocRes.data.data.associations);

      // Fetch trends (last 12 months)
      const trendsParams = new URLSearchParams(params);
      trendsParams.append('months', '12');
      const trendsUrl = `/sda/dashboard/trends?${trendsParams.toString()}`;
      const trendsRes = await api.get(trendsUrl);
      setTrends(trendsRes.data.data.trends);

      // Fetch year-over-year comparison
      const yoyUrl = `/sda/dashboard/year-comparison${params.toString() ? `?${params.toString()}` : ''}`;
      const yoyRes = await api.get(yoyUrl);
      setYearComparison(yoyRes.data.data);

      setLastUpdate(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching SDA dashboard data:', err);
      setError(err.response?.data?.message || 'Errore nel caricamento dei dati della dashboard');
      setLoading(false);
    }
  }, [selectedRegion, selectedSda, selectedSport, selectedYear, showAllAssociations]);

  // Initial load
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Fetch available SDAs for admin
  useEffect(() => {
    if (user?.role === 'admin') {
      api.get('/sda/dashboard/sports')
        .then(res => {
          console.log('Available SDAs response:', res.data);
          const sports = res.data?.data?.sports || [];
          console.log('Processed sports:', sports);
          setAvailableSdas(sports);
        })
        .catch(err => {
          console.error('Error fetching SDAs:', err);
          setAvailableSdas([]);
        });
    }
  }, [user?.role]);

  // Fetch available sports when SDA changes
  useEffect(() => {
    if (selectedSda && user?.role === 'admin') {
      api.get(`/sda/dashboard/disciplines/${encodeURIComponent(selectedSda)}`)
        .then(res => {
          console.log('Available sports for SDA response:', res.data);
          const disciplines = res.data?.data?.disciplines || [];
          console.log('Processed disciplines:', disciplines);
          setAvailableSports(disciplines);
        })
        .catch(err => {
          console.error('Error fetching sports for SDA:', err);
          setAvailableSports([]);
        });
    } else {
      // Reset available sports if no SDA selected
      setAvailableSports([]);
      setSelectedSport('');
    }
  }, [selectedSda, user?.role]);

  // Handle region filter change
  const handleRegionChange = (e) => {
    setSelectedRegion(e.target.value);
  };

  // Handle SDA filter change
  const handleSdaChange = (e) => {
    setSelectedSda(e.target.value);
    // Reset sport selection when SDA changes
    setSelectedSport('');
  };

  // Handle Sport filter change
  const handleSportChange = (e) => {
    setSelectedSport(e.target.value);
  };

  // Handle Year filter change
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // Toggle show all associations
  const handleShowAllAssociations = () => {
    setShowAllAssociations(!showAllAssociations);
  };

  // Function to copy dashboard context for LLM
  const copyDashboardContext = () => {
    if (!summary) return;

    const context = `
DASHBOARD SDA - UISP
Data: ${new Date().toLocaleString('it-IT')}

UTENTE:
- Email: ${user?.email || 'N/A'}
- Ruolo: ${user?.role || 'N/A'}
- SDA: ${selectedSda || summary?.sda || summary?.discipline || user?.discipline || 'Tutte le SDA'}

FILTRI ATTIVI:
${selectedSda ? `- SDA Selezionata: ${selectedSda}` : '- Visualizzazione: Tutte le SDA'}
${selectedRegion ? `- Regione: ${selectedRegion}` : '- Regione: Tutte'}

RIEPILOGO GENERALE:
- Tesserati Totali: ${summary.overview.totalMembers.toLocaleString('it-IT')}
- Associazioni: ${summary.overview.totalAssociations.toLocaleString('it-IT')}
- Nuovi Tesserati (30 giorni): ${summary.overview.newMembers30d.toLocaleString('it-IT')}

DISTRIBUZIONE PER SESSO:
${Object.entries(summary.genderDistribution || {}).map(([gender, count]) => 
  `- ${gender}: ${count.toLocaleString('it-IT')} (${((count / summary.overview.totalMembers) * 100).toFixed(1)}%)`
).join('\n')}

DISTRIBUZIONE PER ETÀ:
${(summary.ageDistribution || []).map(age => 
  `- ${age.ageGroup}: ${age.count.toLocaleString('it-IT')}`
).join('\n')}

TOP 5 REGIONI PER TESSERATI:
${membersByRegion.slice(0, 5).map((r, idx) => 
  `${idx + 1}. ${r.region}: ${r.memberCount.toLocaleString('it-IT')} tesserati, ${r.associationCount} associazioni`
).join('\n')}

TOP 5 TERRITORI PER TESSERATI:
${membersByTerritory.slice(0, 5).map((t, idx) => 
  `${idx + 1}. ${t.territory}: ${t.memberCount.toLocaleString('it-IT')} tesserati, ${t.associationCount} associazioni`
).join('\n')}

TOP 5 ASSOCIAZIONI:
${associations.slice(0, 5).map((a, idx) => 
  `${idx + 1}. ${a.name} (${a.associationCode}) - ${a.city || 'N/A'}, ${a.territory || 'N/A'}: ${a.memberCount} tesserati`
).join('\n')}

TREND ULTIMI 3 MESI:
${trends.slice(-3).map(t => 
  `- ${formatMonth(t.month)}: ${t.newMembers} nuovi tesserati (Cumulativo: ${t.cumulativeMembers})`
).join('\n')}

NOTE:
- Dati aggiornati al: ${lastUpdate.toLocaleString('it-IT')}
- Dashboard filtrata per disciplina sportiva assegnata
`;

    navigator.clipboard.writeText(context).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    }).catch(err => {
      console.error('Errore nella copia:', err);
      alert('Errore nella copia del testo');
    });
  };

  // Format month for charts
  const formatMonth = (month) => {
    if (!month) return '';
    const [year, m] = month.split('-');
    const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return `${months[parseInt(m) - 1]} ${year.slice(2)}`;
  };

  // Prepare gender data for pie chart
  const getGenderData = () => {
    if (!summary?.genderDistribution) return [];
    return Object.entries(summary.genderDistribution).map(([name, value]) => ({
      name,
      value
    }));
  };

  // Prepare age data for bar chart
  const getAgeData = () => {
    if (!summary?.ageDistribution) return [];
    return summary.ageDistribution;
  };

  if (loading && !summary) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status" style={{ color: '#006e39' }}>
          <span className="visually-hidden">Caricamento...</span>
        </Spinner>
        <p className="mt-3">Caricamento dashboard SDA...</p>
      </Container>
    );
  }

  if (error && !summary) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Errore</Alert.Heading>
          <p>{error}</p>
          <Button onClick={fetchDashboardData} variant="outline-danger">Riprova</Button>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="container-fluid min-vh-100" style={{ background: 'linear-gradient(135deg, #006e39, #52b788)' }}>
      <div className="row p-4">
        <div className="col-12">
          <div className="glass-container p-4 rounded-4" style={{ backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            
            {/* Header */}
            <Row className="mb-4">
              <Col>
                <h1 style={{ color: "#006e39" }} className="mb-2">
                  <i className="bi bi-graph-up me-3"></i>
                  Dashboard SDA
                </h1>
                <p className="text-muted mb-0">
                  <i className="bi bi-trophy me-2"></i>
                  SDA: <strong>{selectedSda || summary?.sda || summary?.discipline || user?.discipline || 'Tutte le SDA'}</strong>
                  {' | '}
                  <i className="bi bi-person-badge me-2"></i>
                  {user?.email} - <strong>{user?.role}</strong>
                </p>
                <small className="text-muted">
                  Ultimo aggiornamento: {lastUpdate.toLocaleString('it-IT')}
                </small>
              </Col>
              <Col xs="auto" className="d-flex gap-2 align-items-start">
                <Form.Select
                  size="sm"
                  style={{ width: '150px' }}
                  value={selectedYear}
                  onChange={handleYearChange}
                >
                  <option value="">Tutti gli anni</option>
                  {availableYears.map(y => (
                    <option key={y} value={y.toString()}>{y}</option>
                  ))}
                </Form.Select>
                {user?.role === 'admin' && Array.isArray(availableSdas) && availableSdas.length > 0 && (
                  <Form.Select
                    size="sm"
                    style={{ width: '250px' }}
                    value={selectedSda}
                    onChange={handleSdaChange}
                  >
                    <option value="">Vedi tutte le SDA</option>
                    {availableSdas.map(sda => (
                      <option key={sda.sport} value={sda.sport}>
                        {sda.sport} ({sda.memberCount})
                      </option>
                    ))}
                  </Form.Select>
                )}
                {user?.role === 'admin' && selectedSda && Array.isArray(availableSports) && availableSports.length > 0 && (
                  <Form.Select
                    size="sm"
                    style={{ width: '250px' }}
                    value={selectedSport}
                    onChange={handleSportChange}
                  >
                    <option value="">Tutti gli sport</option>
                    {availableSports.map(sport => (
                      <option key={sport.sport} value={sport.sport}>
                        {sport.sport} ({sport.memberCount})
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Button
                  variant={copySuccess ? "success" : "outline-primary"}
                  onClick={copyDashboardContext}
                  disabled={!summary}
                  size="sm"
                >
                  <i className={`bi ${copySuccess ? 'bi-check-circle' : 'bi-clipboard'} me-2`}></i>
                  {copySuccess ? 'Copiato!' : 'Copia Contesto'}
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/admin/sda-event-planning')}
                  size="sm"
                >
                  <i className="bi bi-calendar-event me-2"></i>Pianifica Eventi
                </Button>
                <Button
                  variant="outline-success"
                  onClick={fetchDashboardData}
                  disabled={loading}
                  size="sm"
                >
                  {loading ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-arrow-clockwise me-2"></i>Aggiorna</>}
                </Button>
              </Col>
            </Row>

            {/* Summary Cards */}
            {summary && (
              <Row className="mb-4">
                <Col md={4}>
                  <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #006e39 !important' }}>
                    <Card.Body className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#006e3915' }}>
                        <i className="bi bi-people-fill fs-2" style={{ color: '#006e39' }}></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-1">Tesserati Totali</h6>
                        <h2 className="mb-0" style={{ color: '#006e39' }}>{summary.overview.totalMembers.toLocaleString('it-IT')}</h2>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #0077b6 !important' }}>
                    <Card.Body className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#0077b615' }}>
                        <i className="bi bi-building fs-2" style={{ color: '#0077b6' }}></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-1">Associazioni</h6>
                        <h2 className="mb-0" style={{ color: '#0077b6' }}>{summary.overview.totalAssociations.toLocaleString('it-IT')}</h2>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #2a9d8f !important' }}>
                    <Card.Body className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#2a9d8f15' }}>
                        <i className="bi bi-person-plus-fill fs-2" style={{ color: '#2a9d8f' }}></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-1">Nuovi (30gg)</h6>
                        <h2 className="mb-0" style={{ color: '#2a9d8f' }}>{summary.overview.newMembers30d.toLocaleString('it-IT')}</h2>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Charts Row 1: Gender & Age Distribution */}
            <Row className="mb-4">
              <Col lg={4}>
                <Card className="h-100">
                  <Card.Header className="bg-white">
                    <strong><i className="bi bi-gender-ambiguous me-2"></i>Distribuzione per Sesso</strong>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={getGenderData()}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {getGenderData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => value.toLocaleString('it-IT')} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={8}>
                <Card className="h-100">
                  <Card.Header className="bg-white">
                    <strong><i className="bi bi-bar-chart-fill me-2"></i>Distribuzione per Età</strong>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={getAgeData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="ageGroup" />
                        <YAxis />
                        <Tooltip formatter={(value) => value.toLocaleString('it-IT')} />
                        <Bar dataKey="count" fill="#006e39" name="Tesserati" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Charts Row 2: Regional Distribution */}
            <Row className="mb-4">
              <Col lg={12}>
                <Card>
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <strong><i className="bi bi-map me-2"></i>Tesserati per Regione</strong>
                    <Badge bg="success">{membersByRegion.length} regioni</Badge>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={membersByRegion.slice(0, 15)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="region" type="category" width={150} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value) => value.toLocaleString('it-IT')} />
                        <Legend />
                        <Bar dataKey="memberCount" fill="#006e39" name="Tesserati" />
                        <Bar dataKey="associationCount" fill="#0077b6" name="Associazioni" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Charts Row 3: Territory Distribution with Filter */}
            <Row className="mb-4">
              <Col lg={12}>
                <Card>
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <strong><i className="bi bi-geo-alt me-2"></i>Tesserati per Territorio</strong>
                    <Form.Select 
                      size="sm" 
                      style={{ width: '200px' }}
                      value={selectedRegion}
                      onChange={handleRegionChange}
                    >
                      <option value="">Tutte le regioni</option>
                      {membersByRegion.map(r => (
                        <option key={r.region} value={r.region}>{r.region}</option>
                      ))}
                    </Form.Select>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={membersByTerritory.slice(0, 15)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="territory" type="category" width={180} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(value) => value.toLocaleString('it-IT')} />
                        <Legend />
                        <Bar dataKey="memberCount" fill="#2a9d8f" name="Tesserati" />
                        <Bar dataKey="associationCount" fill="#e76f51" name="Associazioni" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Charts Row 4: Trends & Top Associations */}
            <Row className="mb-4">
              <Col lg={6}>
                <Card className="h-100">
                  <Card.Header className="bg-white">
                    <strong><i className="bi bi-graph-up-arrow me-2"></i>Trend Tesseramenti (12 mesi)</strong>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={trends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip 
                          labelFormatter={formatMonth}
                          formatter={(value) => value.toLocaleString('it-IT')} 
                        />
                        <Legend />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="newMembers" 
                          stroke="#006e39" 
                          strokeWidth={2}
                          name="Nuovi Tesserati"
                          dot={{ r: 4 }}
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="cumulativeMembers" 
                          stroke="#0077b6" 
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          name="Cumulativo"
                          dot={{ r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="h-100">
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <strong><i className="bi bi-trophy me-2"></i>{showAllAssociations ? 'Tutte le Associazioni' : 'Top 10 Associazioni'}</strong>
                    <Button 
                      variant="sm" 
                      outline
                      size="sm"
                      onClick={handleShowAllAssociations}
                      className={showAllAssociations ? 'btn-outline-warning' : 'btn-outline-primary'}
                    >
                      {showAllAssociations ? '↑ Mostra solo Top 10' : '↓ Mostra tutte'}
                    </Button>
                  </Card.Header>
                  <Card.Body style={{ maxHeight: showAllAssociations ? '600px' : '350px', overflowY: 'auto' }}>
                    {associations.length > 0 ? (
                      <Table striped hover size="sm">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Associazione</th>
                            <th>Territorio</th>
                            <th>Tesserati</th>
                          </tr>
                        </thead>
                        <tbody>
                          {associations.map((assoc, idx) => (
                            <tr key={assoc.id}>
                              <td>
                                {idx < 3 ? (
                                  <Badge bg={idx === 0 ? 'warning' : idx === 1 ? 'secondary' : 'danger'}>
                                    {idx + 1}
                                  </Badge>
                                ) : (
                                  idx + 1
                                )}
                              </td>
                              <td>
                                <div className="fw-bold small">{assoc.name}</div>
                                <small className="text-muted">{assoc.associationCode}</small>
                              </td>
                              <td className="small">
                                {assoc.city && <span>{assoc.city}</span>}
                                {assoc.territory && <div className="text-muted">{assoc.territory}</div>}
                              </td>
                              <td>
                                <Badge bg="success">{assoc.memberCount}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">
                        Nessuna associazione trovata per questa disciplina
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Charts Row 5: Year-over-Year Comparison */}
            {yearComparison && (
              <Row className="mb-4">
                <Col lg={12}>
                  <Card>
                    <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                      <strong>
                        <i className="bi bi-calendar-compare me-2"></i>
                        Confronto Anno su Anno: {yearComparison.previousYear} vs {yearComparison.currentYear}
                      </strong>
                      <small className="text-muted">Periodo: {yearComparison.periodLabel}</small>
                    </Card.Header>
                    <Card.Body>
                      {/* Overview cards */}
                      <Row className="mb-3">
                        <Col md={4}>
                          <Card className="border-0 bg-light text-center p-3">
                            <div className="text-muted small mb-1">Nuovi tesserati {yearComparison.previousYear}</div>
                            <h4 className="mb-0 text-secondary">{(yearComparison.overview[yearComparison.previousYear] || 0).toLocaleString('it-IT')}</h4>
                          </Card>
                        </Col>
                        <Col md={4}>
                          <Card className="border-0 bg-light text-center p-3">
                            <div className="text-muted small mb-1">Nuovi tesserati {yearComparison.currentYear}</div>
                            <h4 className="mb-0" style={{ color: '#006e39' }}>{(yearComparison.overview[yearComparison.currentYear] || 0).toLocaleString('it-IT')}</h4>
                          </Card>
                        </Col>
                        <Col md={4}>
                          <Card
                            className="border-0 text-center p-3"
                            style={{
                              backgroundColor: yearComparison.overview.deltaPercent === null ? '#f8f9fa'
                                : parseFloat(yearComparison.overview.deltaPercent) >= 0 ? '#d4edda' : '#f8d7da'
                            }}
                          >
                            <div className="text-muted small mb-1">Variazione</div>
                            <h4
                              className="mb-0"
                              style={{
                                color: yearComparison.overview.deltaPercent === null ? 'inherit'
                                  : parseFloat(yearComparison.overview.deltaPercent) >= 0 ? '#155724' : '#721c24'
                              }}
                            >
                              {yearComparison.overview.deltaPercent === null
                                ? 'N/D'
                                : `${parseFloat(yearComparison.overview.deltaPercent) >= 0 ? '+' : ''}${yearComparison.overview.deltaPercent}%`
                              }
                            </h4>
                            <small className="text-muted">
                              ({yearComparison.overview.deltaAbsolute >= 0 ? '+' : ''}{yearComparison.overview.deltaAbsolute.toLocaleString('it-IT')} unità)
                            </small>
                          </Card>
                        </Col>
                      </Row>

                      {/* Monthly chart side-by-side */}
                      <Row>
                        <Col lg={7}>
                          <p className="text-muted small mb-2"><strong>Nuovi tesserati mese per mese</strong></p>
                          <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={yearComparison.monthlyComparison}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="monthName" tick={{ fontSize: 11 }} />
                              <YAxis />
                              <Tooltip formatter={(value) => value.toLocaleString('it-IT')} />
                              <Legend />
                              <Bar dataKey={String(yearComparison.previousYear)} fill="#ccc" name={String(yearComparison.previousYear)} />
                              <Bar dataKey={String(yearComparison.currentYear)} fill="#006e39" name={String(yearComparison.currentYear)} />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </Col>
                        <Col lg={5}>
                          <p className="text-muted small mb-2"><strong>Top regioni: variazione %</strong></p>
                          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                            <Table size="sm" striped hover>
                              <thead>
                                <tr>
                                  <th>Regione</th>
                                  <th className="text-end">{yearComparison.previousYear}</th>
                                  <th className="text-end">{yearComparison.currentYear}</th>
                                  <th className="text-end">Δ%</th>
                                </tr>
                              </thead>
                              <tbody>
                                {yearComparison.regionComparison.map(r => {
                                  const delta = r.delta !== null ? parseFloat(r.delta) : null;
                                  return (
                                    <tr key={r.region}>
                                      <td className="small">{r.region}</td>
                                      <td className="text-end small">{(r[yearComparison.previousYear] || 0).toLocaleString('it-IT')}</td>
                                      <td className="text-end small">{(r[yearComparison.currentYear] || 0).toLocaleString('it-IT')}</td>
                                      <td className="text-end">
                                        {delta === null ? (
                                          <Badge bg="secondary">N/D</Badge>
                                        ) : (
                                          <Badge bg={delta >= 0 ? 'success' : 'danger'}>
                                            {delta >= 0 ? '+' : ''}{delta}%
                                          </Badge>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Info Alert */}
            <Alert variant="info" className="d-flex align-items-center">
              <i className="bi bi-info-circle me-3 fs-4"></i>
              <div>
                <strong>Dashboard SDA</strong>
                <p className="mb-0 small">
                  Questa dashboard mostra le statistiche relative alla disciplina <strong>{summary?.discipline || 'assegnata'}</strong>.
                  I dati sono filtrati automaticamente in base al tuo ruolo e disciplina sportiva.
                </p>
              </div>
            </Alert>

          </div>
        </div>
      </div>
    </div>
  );
};

export default SdaDashboard;
